import * as React from 'react';

import { EClusterPinAnchor } from '../../../../../constants/clusterPins/types';
import { TFeatureWithPrecision } from '../../../../../types/map';
import { getPinCenterOffsetX } from '../../../../../utils/ymapsModules/BalloonManager/getBalloonShapeOffsetX';
import * as styles from '../OfferPreviewBalloon.css';
import { ACTIVE_PIN_SELECTOR, REFINE_TRANSFORM_MAX_FRAMES, ROOT_TEST_ID, WAIT_LAYOUT_TICK_MS } from '../constants';
import { buildBalloonTemplate } from '../utils/buildBalloonTemplate';
import { getBalloonPosition, getBalloonTransform, ISafeAreaInsets } from '../utils/getBalloonPosition';

interface IUseBalloonInstanceParams {
  activeFeature: TFeatureWithPrecision;
  anchor: EClusterPinAnchor;
  map: YMaps.Map;
  previewHeight: number;
  previewWidth: number;
  safeArea: ISafeAreaInsets;
  slotId: string;
  ymaps: YMaps.IYMaps;
}

// Реальная ширина отрисованной пилюли активного пина (зависит от цены/иконки).
// Нужна для центрирования и бокового отступа превью; 0 - пилюли в DOM ещё нет.
function getActivePinWidth(): number {
  const pinElement = document.querySelector<HTMLElement>(ACTIVE_PIN_SELECTOR);

  return pinElement ? pinElement.getBoundingClientRect().width : 0;
}

function getBalloonFeaturePositionKey(feature: TFeatureWithPrecision): string {
  const [latitude, longitude] = feature.geometry.coordinates as YMaps.TCoord;

  return `${feature.id ?? ''}:${feature.precision}:${latitude}:${longitude}`;
}

interface IUseBalloonInstanceResult {
  portalContainer: HTMLDivElement | null;
  rootElement: HTMLElement | null;
}

export function useBalloonInstance({
  activeFeature,
  anchor,
  map,
  previewHeight,
  previewWidth,
  safeArea,
  slotId,
  ymaps,
}: IUseBalloonInstanceParams): IUseBalloonInstanceResult {
  const [portalContainer, setPortalContainer] = React.useState<HTMLDivElement | null>(null);
  const [rootElement, setRootElement] = React.useState<HTMLElement | null>(null);
  const featurePositionKey = getBalloonFeaturePositionKey(activeFeature);

  React.useEffect(() => {
    // для боковых пинов гео-точка лежит на краю пилюли — сдвигаем превью к её визуальному центру.
    // Ширину активной пилюли берём из DOM; на момент открытия она может быть ещё не перерисована
    // (ObjectManager красит активный пин асинхронно), поэтому центрирование уточняем после тика.
    const getTransform = () => {
      const activePinWidth = getActivePinWidth();
      const pinCenterOffsetX = getPinCenterOffsetX(anchor, activePinWidth);
      const position = getBalloonPosition(
        map,
        activeFeature,
        pinCenterOffsetX,
        safeArea,
        previewWidth,
        previewHeight,
        activePinWidth,
      );
      const transform = getBalloonTransform(position, pinCenterOffsetX, previewWidth, activePinWidth);

      return transform;
    };

    const content = buildBalloonTemplate({
      slotId,
      rootClassName: styles['root'],
      transform: getTransform(),
      previewWidth,
    });

    // создать и открыть балун
    const balloon = new ymaps.Balloon(map, {
      closeButton: false,
      layout: ymaps.templateLayoutFactory.createClass(content),
    });
    balloon.options.setParent(map.options);
    balloon.open(activeFeature.geometry.coordinates as YMaps.TCoord);

    let rafId = 0;

    // ObjectManager перекрашивает активную пилюлю в отдельном кадре — дожидаемся, пока она
    // появится в DOM (её реальная ширина нужна для центрирования боковых пинов), и уточняем
    // transform. Ограниченный ретрай по кадрам: обычно хватает 1-2, дальше применяем как есть.
    const refineTransform = (rootEl: HTMLElement, attemptsLeft: number) => {
      if (getActivePinWidth() > 0 || attemptsLeft <= 0) {
        rootEl.style.transform = getTransform();

        return;
      }

      rafId = window.requestAnimationFrame(() => refineTransform(rootEl, attemptsLeft - 1));
    };

    // ymaps вставит layout асинхронно — после тика забираем DOM-узлы
    const timeoutId = window.setTimeout(() => {
      const slotEl = document.getElementById(slotId) as HTMLDivElement | null;
      const rootEl = slotEl?.closest<HTMLElement>(`[data-testid="${ROOT_TEST_ID}"]`) ?? null;

      // Для любого anchor ширина пина задаёт отступ превью при открытии сбоку.
      if (rootEl) {
        refineTransform(rootEl, REFINE_TRANSFORM_MAX_FRAMES);
      }

      setPortalContainer(slotEl);
      setRootElement(rootEl);
    }, WAIT_LAYOUT_TICK_MS);

    // снять и закрыть при размонтировании
    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(rafId);
      balloon.close();
      setPortalContainer(null);
      setRootElement(null);
    };
    // activeFeature содержит визуальные флаги пина; они не должны переоткрывать Yandex Balloon.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featurePositionKey, anchor, map, previewHeight, previewWidth, safeArea, slotId, ymaps]);

  return { portalContainer, rootElement };
}
